import { LitElement, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { SkillDetail } from '../../types/team.js';
import * as teamApi from '../../services/team-api.js';

type ModalTab = 'skill' | 'learnings';
type LoadState = 'idle' | 'loading' | 'loaded' | 'error';

@customElement('cp-team-detail-modal')
export class CpTeamDetailModal extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: Boolean }) open = false;
  @property({ type: String }) skillId = '';
  @property({ type: Array }) availableMcpTools: string[] = [];

  @state() private _activeTab: ModalTab = 'skill';
  @state() private _loadState: LoadState = 'idle';
  @state() private _skillDetail: SkillDetail | null = null;
  @state() private _errorMessage = '';

  private _lastLoadedSkillId = '';

  connectedCallback(): void {
    super.connectedCallback();
    document.addEventListener('keydown', this._onKeyDown);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this._onKeyDown);
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('open') && this.open && this.skillId) {
      if (this.skillId !== this._lastLoadedSkillId) {
        this._activeTab = 'skill';
        this._loadSkillDetail();
      }
    }

    if (changed.has('open') && !this.open) {
      this._lastLoadedSkillId = '';
      this._loadState = 'idle';
      this._skillDetail = null;
    }
  }

  private _onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.open) {
      this._close();
    }
  };

  private _close(): void {
    this.dispatchEvent(new CustomEvent('modal-closed', { bubbles: true, composed: true }));
  }

  private _onBackdropClick(e: Event): void {
    if (e.target === e.currentTarget) {
      this._close();
    }
  }

  private async _loadSkillDetail(): Promise<void> {
    if (!this.skillId) return;
    this._loadState = 'loading';
    this._lastLoadedSkillId = this.skillId;

    try {
      this._skillDetail = await teamApi.getSkillDetail(this.skillId);
      this._loadState = 'loaded';
    } catch (e) {
      this._errorMessage = e instanceof Error ? e.message : 'Fehler beim Laden der Skill-Details';
      this._loadState = 'error';
    }
  }

  private _handleEditClick(): void {
    this._close();
    this.dispatchEvent(new CustomEvent('edit-click', {
      detail: { skillId: this.skillId },
      bubbles: true,
      composed: true,
    }));
  }

  private _handleDeleteClick(): void {
    this._close();
    this.dispatchEvent(new CustomEvent('delete-click', {
      detail: {
        skillId: this.skillId,
        skillName: this._skillDetail?.name || this.skillId,
        teamType: this._skillDetail?.teamType,
      },
      bubbles: true,
      composed: true,
    }));
  }

  private _getCategoryBadgeClass(): string {
    const cat = this._skillDetail?.category?.toLowerCase() || '';
    switch (cat) {
      case 'frontend':
      case 'custom': return 'cp-team-card__badge--category-frontend';
      case 'backend': return 'cp-team-card__badge--category-backend';
      case 'domain': return 'cp-team-card__badge--category-domain';
      case 'architect': return 'cp-team-card__badge--category-architect';
      case 'quality': return 'cp-team-card__badge--category-quality';
      case 'po': return 'cp-team-card__badge--category-po';
      case 'review': return 'cp-team-card__badge--category-review';
      default: return 'cp-team-card__badge--category';
    }
  }

  render() {
    if (!this.open) return nothing;

    return html`
      <div class="cp-team-detail-modal__overlay" @click=${this._onBackdropClick}>
        <div class="cp-team-detail-modal__dialog">
          <div class="cp-team-detail-modal__header">
            ${this._loadState === 'loaded' && this._skillDetail ? html`
              <div class="cp-team-detail-modal__title-row">
                <h2 class="cp-team-detail-modal__heading">${this._skillDetail.name}</h2>
                <span class="cp-team-card__badge ${this._getCategoryBadgeClass()}">${this._skillDetail.category}</span>
              </div>
              <div class="cp-team-detail-modal__header-actions">
                <button class="cp-team-detail-modal__header-btn" @click=${this._handleEditClick} title="Bearbeiten">&#9998;</button>
                <button class="cp-team-detail-modal__header-btn cp-team-detail-modal__header-btn--delete" @click=${this._handleDeleteClick} title="Loeschen">&#10005;</button>
              </div>
            ` : html`
              <h2 class="cp-team-detail-modal__heading">Skill Details</h2>
            `}
            <button class="cp-team-detail-modal__close" @click=${this._close}>&times;</button>
          </div>

          ${this._loadState === 'loaded' ? html`
            <nav class="cp-team-detail-modal__tabs">
              <button
                class="cp-team-detail-modal__tab ${this._activeTab === 'skill' ? 'cp-team-detail-modal__tab--active' : ''}"
                @click=${() => { this._activeTab = 'skill'; }}
              >Skill</button>
              <button
                class="cp-team-detail-modal__tab ${this._activeTab === 'learnings' ? 'cp-team-detail-modal__tab--active' : ''}"
                @click=${() => { this._activeTab = 'learnings'; }}
              >
                Learnings
                ${this._skillDetail && this._skillDetail.learningsCount > 0
                  ? html`<span class="cp-team-detail-modal__tab-count">${this._skillDetail.learningsCount}</span>`
                  : nothing}
              </button>
            </nav>
          ` : nothing}

          <div class="cp-team-detail-modal__content">
            ${this._renderContent()}
          </div>
        </div>
      </div>
    `;
  }

  private _renderContent() {
    switch (this._loadState) {
      case 'idle':
      case 'loading':
        return html`<div class="cp-team-detail-modal__loading"><p>Skill-Details werden geladen...</p></div>`;
      case 'error':
        return html`
          <div class="cp-team-detail-modal__error">
            <p>${this._errorMessage}</p>
            <button class="cp-team-detail-modal__retry-btn" @click=${() => { this._lastLoadedSkillId = ''; this._loadSkillDetail(); }}>Erneut versuchen</button>
          </div>
        `;
      case 'loaded':
        return this._activeTab === 'skill' ? this._renderSkillTab() : this._renderLearningsTab();
    }
  }

  private _renderSkillTab() {
    if (!this._skillDetail) return nothing;
    return html`
      <div class="cp-team-detail-modal__skill-content">
        ${this._renderMcpToolsSection()}
        <pre class="cp-team-detail-modal__markdown">${this._skillDetail.skillContent}</pre>
      </div>
    `;
  }

  private _renderMcpToolsSection() {
    if (!this._skillDetail?.mcpTools || this._skillDetail.mcpTools.length === 0) {
      return nothing;
    }

    return html`
      <div class="cp-team-detail-modal__mcp-section">
        <h4 class="cp-team-detail-modal__mcp-title">MCP Tools</h4>
        <div class="cp-team-detail-modal__mcp-tools">
          ${this._skillDetail.mcpTools.map(tool => {
            const isOrphaned = this.availableMcpTools.length > 0 && !this.availableMcpTools.includes(tool);
            return html`
              <span class="cp-team-detail-modal__mcp-badge ${isOrphaned ? 'cp-team-detail-modal__mcp-badge--orphaned' : ''}">
                ${tool}
                ${isOrphaned ? html`<span class="cp-team-detail-modal__mcp-warning">MCP Tool nicht verfuegbar</span>` : nothing}
              </span>
            `;
          })}
        </div>
      </div>
    `;
  }

  private _renderLearningsTab() {
    if (!this._skillDetail) return nothing;
    if (!this._skillDetail.dosAndDontsContent) {
      return html`<div class="cp-team-detail-modal__empty-learnings"><p>Keine Learnings vorhanden</p></div>`;
    }
    return html`<pre class="cp-team-detail-modal__markdown">${this._skillDetail.dosAndDontsContent}</pre>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'cp-team-detail-modal': CpTeamDetailModal;
  }
}
